import * as Sentry from '@sentry/react';

import { ENV, Environment } from 'config/baseConfig';

const SENTRY_DSN = 'https://a24be7b2d21945f8b622478f0342d400@o866882.ingest.sentry.io/5823521';
const SENTRY_TRACES_SAMPLE_RATE = 0.05;

// A well-formed DSN pointing nowhere. beforeSend already drops every event, but
// a bug in the hook would then hit localhost instead of sentry.io — the failure
// is a loud failed request in the network tab rather than a silent leak of dev
// data. Mirrors LOCAL_ONLY_DSN in the backend's sentry_utils.py.
const LOCAL_ONLY_DSN = 'http://dev@localhost/1';

const isDevelopment = ENV === Environment.DEV;

/**
 * Keeps the Sentry API working locally without anything leaving the machine.
 * Every captureException call site still fires; returning null drops the event
 * before it is sent, so it lands in the console instead of sentry.io.
 */
const logLocallyInsteadOfSending = (event: Sentry.ErrorEvent, hint: Sentry.EventHint): null => {
  console.error('[sentry:dev]', hint?.originalException ?? event);
  return null;
};

export const setupSentry = (): void => {
  if (ENV === Environment.PROD || ENV === Environment.STAGING || ENV === Environment.DEV) {
    Sentry.init({
      dsn: isDevelopment ? LOCAL_ONLY_DSN : SENTRY_DSN,
      environment: ENV,
      // Session and replay envelopes bypass beforeSend. The local DSN already
      // keeps them off sentry.io; leaving the integrations out also keeps the
      // console free of their failed requests to localhost.
      integrations: (defaultIntegrations) =>
        isDevelopment
          ? defaultIntegrations.filter((integration) => integration.name !== 'BrowserSession')
          : [
              ...defaultIntegrations,
              Sentry.browserTracingIntegration(),
              Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: false
              })
            ],

      // Transactions bypass beforeSend too.
      tracesSampleRate: isDevelopment ? 0 : SENTRY_TRACES_SAMPLE_RATE,

      // Record all sessions
      replaysSessionSampleRate: 1.0,

      // Dropping every event in beforeSend makes the SDK report the discarded
      // counts back to sentry.io. Harmless data, but still a send.
      sendClientReports: !isDevelopment,

      beforeSend: isDevelopment ? logLocallyInsteadOfSending : undefined
    });
  }
};
